import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AddEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me } = useApp();
  const qc = useQueryClient();
  const weekStart = location.state?.weekStart || new Date().toISOString();

  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => get("/pm/projects") });
  const myProjects = useMemo(() => projects.filter((p) => (p.members || []).some((m) => m.user_id === me?.id)), [projects, me]);

  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), summary: "", hours: "", project_id: "", work_item_id: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: items = [] } = useQuery({
    queryKey: ["project-items", form.project_id],
    queryFn: () => get(`/pm/projects/${form.project_id}/items`),
    enabled: !!form.project_id,
  });
  const myTasks = useMemo(() => items.filter((i) => i.assignee_id === me?.id), [items, me]);

  const submit = useMutation({
    mutationFn: async () => {
      const weeks = await get(`/timesheets/weeks?employee_id=${me?.id}`);
      let week = weeks.find((w) => new Date(w.week_start).toDateString() === new Date(weekStart).toDateString());
      if (!week) week = await post("/timesheets/weeks", { employee_id: me?.id, week_start: weekStart });
      const entries = [...(week.entries || []), {
        date: new Date(form.date).toISOString(), summary: form.summary, hours: form.hours,
        project_id: form.project_id, work_item_id: form.work_item_id,
      }];
      return patch(`/timesheets/weeks/${week.id}`, { entries });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ts-weeks", me?.id] });
      toast.success("Entry added");
      navigate("/timesheets/week");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add entry"),
  });

  const go = () => {
    if (!form.project_id) { toast.error("Project is required"); return; }
    if (!form.work_item_id) { toast.error("Task is required"); return; }
    if (!form.summary || !form.hours) { toast.error("Summary and hours are required"); return; }
    submit.mutate();
  };

  return (
    <div className="max-w-xl">
      <PageHeader title="Add Timesheet Entry" subtitle="Log time against a task from one of your assigned projects." />

      <Section className="mb-4">
        <div className="space-y-4 p-4">
          <div><Label>Date</Label><Input type="date" className="mt-1" data-testid="entry-date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>

          <div>
            <Label>Project *</Label>
            <Select value={form.project_id} onValueChange={(v) => { set("project_id", v); set("work_item_id", ""); }}>
              <SelectTrigger className="mt-1" data-testid="entry-project"><SelectValue placeholder="Select a project" /></SelectTrigger>
              <SelectContent>{myProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            {myProjects.length === 0 && <p className="mt-1 text-xs text-muted-foreground">You're not assigned to any projects yet.</p>}
          </div>

          <div>
            <Label>Task *</Label>
            <Select value={form.work_item_id} onValueChange={(v) => set("work_item_id", v)} disabled={!form.project_id}>
              <SelectTrigger className="mt-1" data-testid="entry-task"><SelectValue placeholder={form.project_id ? "Select a task" : "Select a project first"} /></SelectTrigger>
              <SelectContent>{myTasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
            </Select>
            {form.project_id && myTasks.length === 0 && <p className="mt-1 text-xs text-muted-foreground">No tasks assigned to you on this project.</p>}
          </div>

          <div><Label>Summary</Label><Input data-testid="entry-summary" className="mt-1" placeholder="What did you work on?" value={form.summary} onChange={(e) => set("summary", e.target.value)} /></div>
          <div><Label>Hours</Label><Input type="number" data-testid="entry-hours" className="mt-1" value={form.hours} onChange={(e) => set("hours", Number(e.target.value))} /></div>
        </div>
      </Section>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate("/timesheets/week")}>Cancel</Button>
        <Button data-testid="entry-submit" onClick={go} disabled={submit.isPending}>Add Entry</Button>
      </div>
    </div>
  );
}
